import React from "react";
import {
  type Booking,
  useScheduleData,
  getPharmacistAvatarProps,
} from "../scheduleData";
import { getPatientConsultationByConsultationId } from "../../../dispatchers/patientDispatchers";

interface BookingCardProps {
  booking: Booking;
  serviceColors: Record<string, string>;
  serviceColorsFromData?: Record<string, string>;
  onEdit: (bookingId: string, bookingData: any) => void;
  onDelete: (bookingId: string) => void;
  pharmacistNames: string[];
  rooms: string[];
  size?: "small" | "medium" | "large";
  isReadOnly?: boolean;
  isDarkMode?: boolean;
}

export function BookingCard({
  booking,
  serviceColors,
  serviceColorsFromData = {},
  onEdit,
  onDelete,
  pharmacistNames,
  rooms,
  size = "medium",
  isReadOnly = false,
  isDarkMode = false,
}: BookingCardProps) {
  const { pharmacists, currentPharmacyId } = useScheduleData();

  const isOtherPharmacy =
    Boolean(booking.otherPharmacyId) && String(booking.otherPharmacyId) !== String(currentPharmacyId);

  // Find the pharmacist object for avatar/initials
  let avatarProps = null;
  if (
    booking.pharmacist &&
    booking.pharmacist.toLowerCase() !== "unknown pharmacist"
  ) {
    const pharmacistProfile = pharmacists.find(
      (p) => p.name === booking.pharmacist,
    );
    avatarProps = pharmacistProfile
      ? getPharmacistAvatarProps(
          pharmacistProfile,
          size === "small" ? "sm" : size === "medium" ? "md" : "lg",
        )
      : null;
  }

  const handleBookingClick = async () => {
    if (isReadOnly) return;
    await getPatientConsultationByConsultationId(booking?.consultationID);
    onEdit(booking.id, booking);
  };

  const avatarSize =
    size === "small" ? "w-5 h-5" : size === "medium" ? "w-6 h-6" : "w-10 h-10";
  const deleteButtonSize =
    size === "small" ? "h-3 w-3" : size === "medium" ? "h-4 w-4" : "h-6 w-6";
  const deleteIconSize =
    size === "small" ? "w-2 h-2" : size === "medium" ? "w-2 h-2" : "w-3 h-3";

  // Get color from service data first, then hex color from booking, then fallback to service type mapping
  const bookingColor =
    booking.serviceColor ||
    serviceColorsFromData[booking.serviceName] ||
    serviceColors[booking.serviceType] ||
    "#6b7280"; // default gray hex
  const isCancelledBooking = booking.eventStatus === 4;
  const cancelledSuffix = isCancelledBooking ? " (Cancelled)" : "";

  const isHexColor = bookingColor.startsWith("#");

  // Use the same service color for all bookings, including other pharmacy events
  const bgStyle =
    isCancelledBooking || !isHexColor
      ? {}
      : {
          backgroundColor: bookingColor,
          borderColor: isHexColor ? bookingColor : undefined,
          borderWidth: "0px",
        };

  const colorClassName = isCancelledBooking
    ? "bg-red-600 text-white border border-red-600"
    : isHexColor
      ? "text-white shadow-sm"
      : bookingColor;

  if (size === "large") {
    return (
      <>
        <div
          className="group relative pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`p-3 rounded-lg ${isReadOnly ? "" : "cursor-pointer hover:opacity-80"} transition-opacity ${colorClassName}`}
            style={bgStyle}
            title={`${booking.patientName} - ${booking.serviceName}${cancelledSuffix} - ${booking.pharmacist} - ${booking.time}`}
            onClick={(e) => {
              e.stopPropagation();
              handleBookingClick();
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                {avatarProps &&
                  (avatarProps.type === "image" ? (
                    <img
                      src={avatarProps.src}
                      alt={avatarProps.alt}
                      className={`${avatarSize} rounded-full object-cover flex-shrink-0 mt-1 border-2 border-white shadow-sm`}
                    />
                  ) : (
                    <div
                      className={`${avatarSize} rounded-full flex items-center justify-center flex-shrink-0 mt-1 border-2 border-white shadow-sm bg-blue-500 text-white font-medium text-xs`}
                    >
                      {avatarProps.initials}
                    </div>
                  ))}
                <div>
                  <div className="font-medium">
                    {booking.time} - {booking.serviceName}
                    {cancelledSuffix}
                  </div>
                  <div className="text-sm opacity-75">
                    Patient: {booking.patientName}
                  </div>
                  {booking.notes && (
                    <div className="text-sm font-medium opacity-90 italic">
                      Notes: {booking.notes}
                    </div>
                  )}
                  {booking.otherPharmacyName && (
                    <div className="mt-2 text-[10px] text-gray-500 bg-white border border-gray-300 rounded-full px-3 py-1 w-fit shadow-sm">
                      {booking.otherPharmacyName}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-sm">{booking.duration} min</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div
        className="group relative pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`${booking.isMyAppointments ? "p-1.5" : "text-xs p-1"} rounded ${isReadOnly ? "" : "cursor-pointer hover:opacity-80"} transition-opacity ${colorClassName}`}
          style={bgStyle}
          title={`${booking.patientName} - ${booking.serviceName}${cancelledSuffix} - ${booking.pharmacist} - ${booking.time}`}
          onClick={(e) => {
            e.stopPropagation();
            handleBookingClick();
          }}
        >
          <div className="flex items-center gap-1 mb-1">
            {avatarProps &&
              (avatarProps.type === "image" ? (
                <img
                  src={avatarProps.src}
                  alt={avatarProps.alt}
                  className={`${avatarSize} rounded-full object-cover flex-shrink-0 border-2 border-white shadow-sm`}
                />
              ) : (
                <div
                  className={`${avatarSize} rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white shadow-sm bg-blue-500 text-white font-medium text-xs`}
                >
                  {avatarProps.initials}
                </div>
              ))}
            <div
              className={`${booking.isMyAppointments ? "font-bold text-[14px]" : "font-medium"} truncate ${
                size === "small" ? "" : "text-center flex-1"
              }`}
            >
              {size === "small"
                ? booking.serviceName
                : `${booking.time} - ${booking.serviceName}${cancelledSuffix}`}
            </div>
          </div>
          <div
            className={`truncate ${booking.isMyAppointments ? "font-semibold opacity-100 text-[12px]" : "opacity-75"} ${
              size === "small" ? "" : "text-center"
            }`}
          >
            {booking.patientName}
          </div>
          {booking.isMyAppointments && booking.notes && size !== "small" && (
            <div className="truncate opacity-90 text-[11px] text-center italic font-semibold leading-tight">
              {booking.notes}
            </div>
          )}
          {isOtherPharmacy && booking.otherPharmacyName && (
            <div className="mt-1.5 flex justify-center">
              <div className="text-[10px] font-bold text-gray-700 bg-white border border-gray-400 rounded-full px-2.5 py-0.5 shadow-sm max-w-full truncate">
                {booking.otherPharmacyName}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
